import { Injectable } from '@angular/core';
import {
  Map,
  icon,
  LatLngExpression,
  Marker,
  tileLayer,
  marker,
  featureGroup,
  Layer,
  LatLng,
  divIcon,
  FeatureGroup,
} from 'leaflet';
import * as lfGeo from 'leaflet.geodesic';
import { GestureHandling } from 'leaflet-gesture-handling';
import { MapData } from 'app/features/export/map-projection/model/mapData';
import L from 'leaflet';
import { ActivatedRoute } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class MapService {
  /**
   * Map Service is used in 3 places, the Flight Detail View, Flight Sequence View and Career Map Projection.
   * Two differenct coord LatLng sets will be passed in independently since the
   * Sequence View passes in dep/arr with [][] of coord and Flight Details only passes in one [] of
   * dep/arr coords.  The mapProjData is a different model passed in to render the career map.
   * To determine which view is making the request the mapId, which is the element id of the map is passed in as a string as either
   * 'legMap', 'seqMap' or 'projMap'.  The mapId is used to determine which coords to use as well as
   * which map to render.
   */
  constructor(private route: ActivatedRoute) {}

  fltDetialcoords?: LatLngExpression[];
  seqDetailCoords?: LatLngExpression[][];
  mapProjData?: MapData[];
  geoLineColor?: string;
  geoLine?: lfGeo.GeodesicLine;
  stationMarker?: Marker;
  isDetailView?: number;
  multiGeoLine?: boolean;
  featureGroup?: FeatureGroup<any>;
  private map?: Map;
  private readonly cartoKey = 'cb1_4lkx_1_5fc435807edcf7f4d4444ac7';

  renderMap(
    mapId: string,
    fltDetailCoords?: LatLngExpression[],
    seqDetailCoords?: LatLngExpression[][],
    mapProjData?: MapData[],
    geoMulitLineColor?: boolean,
    geoLineColor?: string,
  ): void {
    if (mapId === 'legMap' && fltDetailCoords) {
      this.fltDetialcoords = fltDetailCoords;
    } else if (mapId === 'seqMap' && seqDetailCoords) {
      this.seqDetailCoords = seqDetailCoords;
    } else {
      //projMap
      this.mapProjData = mapProjData;
      this.multiGeoLine = geoMulitLineColor;
    }

    if (geoLineColor) {
      this.geoLineColor = geoLineColor;
    }
    this.initMap(mapId);
  }

  // Initializes the map with the given mapId.
  initMap(mapId: string): void {
    if (!this.map) {
      this.map = new Map(mapId);
    } else {
      this.map.remove();
      this.map = new Map(mapId);
    }
    if (this.map) {
      this.map.addHandler('gestureHandling', GestureHandling);
      // @ts-expect-error typescript does not see handler.
      this.map.gestureHandling.enable();

      const tiles = tileLayer(
        `https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png?key=${this.cartoKey}`,
        {
          detectRetina: true,
          subdomains: 'abcd',
          attribution:
            '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>, &copy; <a href="https://carto.com/attributions">CARTO</a>',
          maxZoom: 20,
        },
      );
      tiles.addTo(this.map);

      //Leg Map
      if (this.fltDetialcoords && mapId === 'legMap') {
        for (let coord of this.fltDetialcoords) {
          let staMarker = marker(coord, {
            icon: icon({
              iconSize: [12, 20],
              iconAnchor: [6, 20],
              iconUrl: 'assets/marker-icon-2x.png',
              iconRetinaUrl: 'assets/marker-icon-2x.png',
              shadowSize: [0, 0],
            }),
          });
          staMarker.addTo(this.map);
        }
        this.geoLine = new lfGeo.GeodesicLine(this.fltDetialcoords, {
          color: this.geoLineColor,
          weight: 1.5,
          wrap: false,
        }).addTo(this.map);
        this.geoLine.addTo(this.map);
        this.map.attributionControl.setPrefix('');
        this.map.fitBounds(this.geoLine.getBounds());
      }

      //Sequence Map
      else if (this.seqDetailCoords && mapId === 'seqMap') {
        let markers: Layer[] = [];
        for (let staCoords of this.seqDetailCoords) {
          staCoords.forEach((coord) => {
            let staMarker = marker(coord, {
              icon: icon({
                iconSize: [12, 20],
                iconAnchor: [6, 20],
                iconUrl: 'assets/marker-icon-2x.png',
                iconRetinaUrl: 'assets/marker-icon-2x.png',
                shadowSize: [0, 0],
              }),
            });

            markers.push(staMarker);
          });
          featureGroup(markers).addTo(this.map);
        }

        this.geoLine = new lfGeo.GeodesicLine(this.seqDetailCoords, {
          color: 'blue',
          weight: 1.5,
          wrap: false,
        }).addTo(this.map);
        this.geoLine.addTo(this.map);
        this.map.attributionControl.setPrefix('');
        this.map.fitBounds(this.geoLine.getBounds());
      }

      //Career Map Projection
      else if (this.mapProjData && mapId === 'projMap') {
        const coords = this.getMapCoords(this.mapProjData);
        let markers: Layer[] = [];
        const seenStations = new Set<string>();

        for (let staCoords of coords) {
          staCoords.coords.forEach((coord, index) => {
            const stationName = index === 0 ? staCoords.dep : staCoords.arr;
            if (!seenStations.has(stationName)) {
              seenStations.add(stationName);
            }
            // Styling references found in main.scss
            const textIcon = divIcon({
              html: `<div class="map-marker-text">${stationName}</div>`,
              className: '', // Let the inner div handle the class
              iconSize: undefined, // removes white background div
              iconAnchor: [10, 15],
            });
            let staMarker = marker(coord, {
              icon: textIcon,
              interactive: false,
            });

            markers.push(staMarker);
          });

          this.featureGroup = featureGroup(markers);

          this.geoLine = new lfGeo.GeodesicLine(staCoords.coords, {
            color: staCoords.color,
            weight: 1,
            wrap: false,
          }).addTo(this.map);
          this.geoLine.addTo(this.map);

          if (this.map && this.mapProjData) {
            // Remove existing legend if present
            const existingLegend = document.getElementById('map-legend');
            if (existingLegend) {
              existingLegend.remove();
            }

            // Create legend container
            //  Styling references found in main.scss
            if (this.multiGeoLine) {
              const legend = new L.Control({ position: 'bottomright' });
              legend.onAdd = () => {
                const div = L.DomUtil.create('div', 'info legend');
                div.id = 'map-legend';
                div.style.background = 'white';
                div.style.padding = '8px';
                div.style.borderRadius = '4px';
                div.style.boxShadow = '0 1px 4px rgba(0,0,0,0.2)';
                div.innerHTML =
                  '<strong class="map-legend-title">Legend</strong><br>';

                const seen = new Set<string>();
                this.mapProjData?.forEach((point, idx) => {
                  const color = point.geoLineColor || 'blue';
                  const aircraft = point.aircraft || `Aircraft ${idx + 1}`;
                  const key = `${aircraft}|${color}`;
                  if (!seen.has(key)) {
                    seen.add(key);
                    div.innerHTML += `
                  <div class="map-legend-container">
                    <span class="map-legend-color-item" style="background:${color}"></span>
                    <span>${aircraft}</span>
                  </div>
                  `;
                  }
                });
                return div;
              };
              legend.addTo(this.map);
            }
          }
        }

        this.map.attributionControl.setPrefix('');
        this.map.zoomControl.setPosition('topright');
        this.map.setView([30.0, -98.5795], 4);
      }
    }
  }

  getMapCoords(
    mapData: MapData[],
  ): { coords: LatLngExpression[]; color: string; dep: string; arr: string }[] {
    let coords: LatLngExpression[][] = [];
    let color: string[] = [];
    let dep: string[] = [];
    let arr: string[] = [];
    if (mapData) {
      mapData.forEach((point) => {
        const latlng = [
          new LatLng(point.depature.lat, point.depature.long),
          new LatLng(point.arrival.lat, point.arrival.long),
        ];
        coords.push(latlng);
        color.push(point.geoLineColor || 'blue');
        dep.push(point.depature.station);
        arr.push(point.arrival.station);
      });
    }
    const data = coords.map((coord, i) => {
      return {
        coords: coord,
        color: color[i],
        dep: dep[i],
        arr: arr[i],
      };
    });
    return data;
  }

  //Methods to hide and show markers on the Career Map Projection ONLY
  hideMarkers(): void {
    if (this.map && this.featureGroup) {
      this.map.removeLayer(this.featureGroup);
    }
  }

  showMarkers(): void {
    if (this.map && this.featureGroup) {
      this.featureGroup.addTo(this.map);
    }
  }
}
